/* eslint no-param-reassign: "error" */
import {
  createSlice,
} from '@reduxjs/toolkit';

import type { EntityDataPubType } from '@/domain/entityBase';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type {
  ChildApplyJsonPub,
  ChildApplyJsonPvt,
} from '@/types/domain/childApply/entities/childApplyEntity';
import type {
  ChildRemoveJsonPub,
  ChildRemoveJsonPvt,
} from '@/types/domain/childApply/entities/childRemoveEntity';
import type {
  FacilityJsonPub,
  FacilityJsonPvt,
} from '@/types/domain/childApply/entities/facilityEntity';
import type { HomepageSettingsJson } from '@/types/domain/childApply/entities/homepageSettingsEntity';
import type { ApplyNurseryType, ChildApplyStateType } from '@/types/domain/childApply/redux';
import type {
  PayloadAction,
} from '@reduxjs/toolkit';

import { service } from '@/share';

const initialState: ChildApplyStateType = {
  childApplyList: [],
  nextTokenChildApply: null,
  childRemoveList: [],
  nextTokenChildRemove: null,
  facilityList: [],
  applyNurseryList: null,
  facilityHomepageSettingsList: [],
};

/**
 * 子ども申請Slice
 */
export const childApplySlice = createSlice({
  name: 'childApply',
  initialState,
  reducers: {
    /** 子ども申請リストをセット */
    setChildApplyList:
      (state, action: PayloadAction<{
        nextToken: string,
        items: Array<EntityDataPubType<ChildApplyJsonPvt, ChildApplyJsonPub>>,
      }>) => {
        state.childApplyList = action.payload.items;
        state.nextTokenChildApply = action.payload.nextToken;
      },
    /** 作成 */
    createChildApplyStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      state.childApplyList = service.childApply.createChildApplyStore(
        state.childApplyList,
        action.payload,
        undefined,
      );
    },
    /** 更新 */
    updateChildApplyStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      state.childApplyList = service.childApply.updateChildApplyStore(
        state.childApplyList,
        action.payload,
      );
    },
    /** 削除 */
    deleteChildApplyStore: (
      state,
      action: PayloadAction<DeleteOutputType>,
    ) => {
      state.childApplyList = service.childApply.deleteChildApplyStore(
        state.childApplyList,
        action.payload,
      );
    },
    /** 退園をセット */
    setChildRemoveList:
      (state, action: PayloadAction<{
        nextToken: string,
        items: Array<EntityDataPubType<ChildRemoveJsonPvt, ChildRemoveJsonPub>>,
      }>) => {
        state.childRemoveList = action.payload.items;
        state.nextTokenChildRemove = action.payload.nextToken;
      },
    /** 作成 */
    createChildRemoveStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      state.childRemoveList = service.childApply.createChildRemoveStore(
        state.childRemoveList,
        action.payload,
        undefined,
      );
    },
    /** 更新 */
    updateChildRemoveStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      state.childRemoveList = service.childApply.updateChildRemoveStore(
        state.childRemoveList,
        action.payload,
      );
    },
    /** 削除 */
    deleteChildRemoveStore: (
      state,
      action: PayloadAction<DeleteOutputType>,
    ) => {
      state.childRemoveList = service.childApply.deleteChildRemoveStore(
        state.childRemoveList,
        action.payload,
      );
    },
    /** setFacilityList */
    setFacilityList: (
      state,
      action: PayloadAction<Array<EntityDataPubType<FacilityJsonPvt, FacilityJsonPub>>>,
    ) => {
      state.facilityList = action.payload;
    },
    /** setApplyNurseryList */
    setApplyNurseryList: (
      state,
      action: PayloadAction<ApplyNurseryType[] | null>,
    ) => {
      state.applyNurseryList = action.payload;
    },
    /** 作成 */
    createFacilityStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      state.facilityList = service.childApply.createFacilityStore(
        state.facilityList,
        action.payload,
        undefined,
      );
    },
    /** 更新 */
    updateFacilityStore: (state, action: PayloadAction<EntitySubscribeType>) => {
      state.facilityList = service.childApply.updateFacilityStore(
        state.facilityList,
        action.payload,
      );
    },
    /** 削除 */
    deleteFacilityStore: (state, action: PayloadAction<DeleteOutputType>) => {
      state.facilityList = service.childApply.deleteFacilityStore(
        state.facilityList,
        action.payload,
      );
    },
    /** setFacilityHomepageSettingsList */
    setFacilityHomepageSettingsList: (
      state,
      action: PayloadAction<Array<HomepageSettingsJson>>,
    ) => {
      state.facilityHomepageSettingsList = action.payload;
    },
  },
});
